/**
 * Busca de lugares e interesses na Meta, com chips.
 *
 * Usa `targeting.search` (a mesma busca do Gerenciador) com debounce: cada
 * tecla é uma chamada à Graph, que conta no limite da conta.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/useDebounce";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, X } from "lucide-react";

type Item = { key: string; name: string; type: string; path?: string[] | null; audienceSize?: number | null; countryCode?: string | null };

const TIPO_LOCAL: Record<string, string> = { country: "país", region: "estado", city: "cidade", zip: "CEP", geo_market: "região" };

export function TargetingPicker({
  tipo,
  selecionados,
  onChange,
  placeholder,
  max = 25,
}: {
  tipo: "adgeolocation" | "adinterest";
  selecionados: Array<{ key: string; name: string; type?: string; countryCode?: string | null }>;
  onChange: (itens: Array<{ key: string; name: string; type: string; countryCode?: string | null }>) => void;
  placeholder: string;
  max?: number;
}) {
  const [q, setQ] = useState("");
  const termo = useDebounce(q.trim(), 350);
  const busca = trpc.metaAds.targeting.search.useQuery(
    { q: termo, type: tipo, limit: 8 },
    { enabled: termo.length >= 2, staleTime: 5 * 60_000 },
  );

  const adicionar = (i: Item) => {
    if (selecionados.some((s) => s.key === i.key)) return;
    if (selecionados.length >= max) return;
    onChange([...selecionados.map((s) => ({ key: s.key, name: s.name, type: s.type ?? (tipo === "adinterest" ? "interest" : "city"), countryCode: s.countryCode ?? null })), { key: i.key, name: i.name, type: i.type, countryCode: i.countryCode ?? null }]);
    setQ("");
  };
  const remover = (key: string) =>
    onChange(selecionados.filter((s) => s.key !== key).map((s) => ({ key: s.key, name: s.name, type: s.type ?? "city", countryCode: s.countryCode ?? null })));

  const resultados = (busca.data ?? []) as Item[];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {selecionados.map((s) => (
          <Badge key={s.key} variant="secondary" className="gap-1 pr-1">
            {s.name}
            {tipo === "adgeolocation" && s.type && <span className="text-[10px] text-muted-foreground">({TIPO_LOCAL[s.type] ?? s.type})</span>}
            <button type="button" onClick={() => remover(s.key)} className="rounded-full p-0.5 hover:bg-muted" aria-label={`Remover ${s.name}`}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="relative">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} disabled={selecionados.length >= max} />
        {busca.isFetching && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
        {termo.length >= 2 && !busca.isFetching && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
            {resultados.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">Nada encontrado na Meta.</p>}
            {resultados.map((r) => (
              <button
                key={`${r.type}:${r.key}`}
                type="button"
                onClick={() => adicionar(r)}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="min-w-0">
                  <span className="block truncate">{r.name}</span>
                  {r.path && r.path.length > 0 && <span className="block truncate text-xs text-muted-foreground">{r.path.join(" › ")}</span>}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {tipo === "adgeolocation" ? TIPO_LOCAL[r.type] ?? r.type : r.audienceSize ? `${new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(r.audienceSize)} pessoas` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
